function buildProcessingDeadlineBatchItem(item) {
  return {
    localItemId: item?.id || '',
    caseId: item?.caseId || '',
    mediaId: item?.mediaId || '',
    transcriptFileId: item?.transcriptFileId || '',
    mediaStoredFileName: item?.mediaStoredFileName || item?.mediaFile?.name || '',
    transcriptStoredFileName: item?.transcriptStoredFileName || item?.transcriptFile?.name || '',
    mediaOriginalFileName: item?.mediaFile?.name || '',
    transcriptOriginalFileName: item?.transcriptFile?.name || '',
    transcriptEnabled: Boolean(item?.transcriptEnabled)
  };
}

function parseUtcTimestampMs(value) {
  const parsedTime = Date.parse(String(value || '').trim());
  return Number.isFinite(parsedTime) ? parsedTime : null;
}

function buildTimedOutRemoteScanCandidateRequestKey(candidate) {
  return [
    candidate?.caseId || '',
    candidate?.mediaId || '',
    candidate?.transcriptFileId || '',
    candidate?.storedFileName || '',
    candidate?.isTranscript ? 'transcript' : 'media'
  ].join('|');
}

function hasEqualNormalizedValue(recordValue, batchValue) {
  return Boolean(batchValue) && recordValue === batchValue;
}

function matchesAnyNormalizedValue(pairs) {
  return pairs.some(([recordValue, batchValue]) => hasEqualNormalizedValue(recordValue, batchValue));
}

export function createRemoteProcessingTimeoutHelpers({
  normalizeDisplayName,
  getFirstRecordValue,
  getRecordMediaId,
  getRecordTranscriptFileId,
  getRecordStoredFileName,
  getRecordOriginalFileName,
  getRecordSubmittedAtUtc,
  getRecordScanStatus,
  isTranscriptCaseRecord,
  isPendingRemoteScanRecord,
  dedupeCaseFileRecords,
  buildCaseFileKey,
  isTerminalScanStatus,
  defaultMaxProcessingSeconds
}) {
  function doesDeadlineBatchItemMatchRecord(record, batchItem, isTranscript) {
    if (!record || !batchItem) {
      return false;
    }

    const recordCaseId = normalizeDisplayName(getFirstRecordValue(record, ['caseId', 'CaseId']));
    const batchCaseId = normalizeDisplayName(batchItem.caseId);
    if (batchCaseId && recordCaseId && recordCaseId !== batchCaseId) {
      return false;
    }

    const normalizedRecord = {
      mediaId: normalizeDisplayName(getRecordMediaId(record)),
      transcriptFileId: normalizeDisplayName(getRecordTranscriptFileId(record)),
      storedFileName: normalizeDisplayName(getRecordStoredFileName(record)),
      originalFileName: normalizeDisplayName(getRecordOriginalFileName(record))
    };

    const normalizedBatch = {
      mediaId: normalizeDisplayName(batchItem.mediaId),
      mediaStoredFileName: normalizeDisplayName(batchItem.mediaStoredFileName),
      mediaOriginalFileName: normalizeDisplayName(batchItem.mediaOriginalFileName),
      transcriptFileId: normalizeDisplayName(batchItem.transcriptFileId),
      transcriptStoredFileName: normalizeDisplayName(batchItem.transcriptStoredFileName),
      transcriptOriginalFileName: normalizeDisplayName(batchItem.transcriptOriginalFileName)
    };

    if (isTranscript) {
      return matchesAnyNormalizedValue([
        [normalizedRecord.transcriptFileId, normalizedBatch.transcriptFileId],
        [normalizedRecord.storedFileName, normalizedBatch.transcriptStoredFileName],
        [normalizedRecord.originalFileName, normalizedBatch.transcriptOriginalFileName],
        [normalizedRecord.mediaId, normalizedBatch.mediaId]
      ]);
    }

    return matchesAnyNormalizedValue([
      [normalizedRecord.mediaId, normalizedBatch.mediaId],
      [normalizedRecord.storedFileName, normalizedBatch.mediaStoredFileName],
      [normalizedRecord.originalFileName, normalizedBatch.mediaOriginalFileName]
    ]);
  }

  function mapRemoteScanRecordToTimedOutCandidate(record) {
    return {
      applicationId: getFirstRecordValue(record, ['clientApplicationId', 'ClientApplicationId', 'applicationId', 'ApplicationId']) || '',
      caseId: getFirstRecordValue(record, ['caseId', 'CaseId']) || '',
      mediaId: getRecordMediaId(record) || '',
      transcriptFileId: getRecordTranscriptFileId(record) || '',
      storedFileName: getRecordStoredFileName(record) || '',
      submittedAtUtc: getRecordSubmittedAtUtc(record) || '',
      scanStatus: getRecordScanStatus(record) || '',
      isTranscript: isTranscriptCaseRecord(record)
    };
  }

  function collectDeadlineMatchedRemoteScanCandidates(records, batchItems) {
    const normalizedBatchItems = Array.isArray(batchItems) ? batchItems : [];
    if (normalizedBatchItems.length === 0) {
      return [];
    }

    return dedupeCaseFileRecords(records ?? [])
      .filter((record) => {
        if (!isPendingRemoteScanRecord(record)) {
          return false;
        }

        const isTranscript = isTranscriptCaseRecord(record);
        return normalizedBatchItems.some((batchItem) => doesDeadlineBatchItemMatchRecord(record, batchItem, isTranscript));
      })
      .map(mapRemoteScanRecordToTimedOutCandidate);
  }

  function isRemoteScanRecordPastProcessingDeadline(
    record,
    nowUtc = new Date(),
    maxProcessingSeconds = defaultMaxProcessingSeconds
  ) {
    if (!record || !isPendingRemoteScanRecord(record)) {
      return false;
    }

    const submittedAtTime = parseUtcTimestampMs(getRecordSubmittedAtUtc(record));
    if (submittedAtTime === null) {
      return false;
    }

    const currentTime = nowUtc instanceof Date ? nowUtc.getTime() : Date.parse(nowUtc);
    if (!Number.isFinite(currentTime)) {
      return false;
    }

    return ((currentTime - submittedAtTime) / 1000) > maxProcessingSeconds;
  }

  function collectSubmittedAtTimedOutRemoteScanCandidates(
    records,
    nowUtc = new Date(),
    maxProcessingSeconds = defaultMaxProcessingSeconds
  ) {
    return dedupeCaseFileRecords(records ?? [])
      .filter((record) => isRemoteScanRecordPastProcessingDeadline(record, nowUtc, maxProcessingSeconds))
      .map(mapRemoteScanRecordToTimedOutCandidate);
  }

  function buildPendingScanFileKeys(items, applicationNumber, caseId) {
    const keys = new Set();
    const normalizedApplicationNumber = normalizeDisplayName(applicationNumber);
    const normalizedCaseId = normalizeDisplayName(caseId);

    (items ?? []).forEach((item) => {
      const mediaId = normalizeDisplayName(item?.mediaId);
      if (!mediaId) {
        return;
      }

      if (item?.mediaUploadStatus === 'completed' && !isTerminalScanStatus(item?.scanStatus)) {
        keys.add(buildCaseFileKey({
          applicationNumber: normalizedApplicationNumber,
          caseId: normalizedCaseId,
          mediaId,
          fileType: 'Media'
        }));
      }

      if (item?.transcriptEnabled && item?.transcriptUploadStatus === 'completed' && !isTerminalScanStatus(item?.transcriptScanStatus)) {
        keys.add(buildCaseFileKey({
          applicationNumber: normalizedApplicationNumber,
          caseId: normalizedCaseId,
          mediaId,
          fileType: 'Transcript'
        }));
      }
    });

    return Array.from(keys.values()).filter(Boolean);
  }

  return {
    buildProcessingDeadlineBatchItem,
    collectDeadlineMatchedRemoteScanCandidates,
    parseUtcTimestampMs,
    isRemoteScanRecordPastProcessingDeadline,
    mapRemoteScanRecordToTimedOutCandidate,
    collectSubmittedAtTimedOutRemoteScanCandidates,
    buildTimedOutRemoteScanCandidateRequestKey,
    buildPendingScanFileKeys
  };
}
